import React, { useMemo } from 'react';
import { ValueAxis, ArgumentAxis, Series, Legend } from 'devextreme-react/chart';
import { NuvoBarChart } from '@nuvolo/nuux-chart/components/NuvoBarChart';
import type { NuvoPieChartProps } from '@nuvolo/nuux-chart/components/NuvoPieChart';
import { DashboardStatsPoint } from '#/graphql/types';
import { maxBy } from 'lodash';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import FAIcon from '#/common/components/FAIcon';

export interface BarChartProps {
  title?: string;
  noDataText?: string;
  dataSource: (DashboardStatsPoint | null)[] | undefined | null;
  startingDataColor?: NuvoPieChartProps['startingDataColor'];
  setup: {
    valueAxis: string;
    argumentAxis: string;
  };
  onItemClick?: (item: DashboardStatsPoint) => void;
  yTickInterval?: number;
  useLogarithmicScale?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  noDataText = 'No data to display!',
  dataSource,
  setup,
  onItemClick,
  startingDataColor = 'red',
  yTickInterval,
  useLogarithmicScale = false,
}) => {
  const ds = useMemo(() => {
    if (!dataSource) {
      return [];
    }

    return dataSource?.map((v) => ({
      ...v,
      value: v?.value || 0,
    }));
  }, [dataSource]);

  // Auto-detect if logarithmic scale is needed (when max/min ratio > 1000)
  const shouldUseLogScale = useMemo(() => {
    if (useLogarithmicScale) return true;
    if (!ds || ds.length < 2) return false;

    const positiveValues = ds.map((d) => d.value).filter((v) => v > 0);
    if (positiveValues.length < 2) return false;

    const maxVal = Math.max(...positiveValues);
    const minVal = Math.min(...positiveValues);

    // Use log scale if the ratio between max and min is greater than 1000
    return minVal > 0 && maxVal / minVal > 1000;
  }, [ds, useLogarithmicScale]);

  const hasData = ds && ds.length > 0;
  console.log('BarChart dataSource:', dataSource);
  if (!hasData) {
    return (
      <NuvoBox
        height="100%"
        minHeight="300px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        flex={1}>
        <FAIcon icon="shapes" size="xl" />
        <NuvoText type="body">{noDataText}</NuvoText>
      </NuvoBox>
    );
  }

  return (
    <NuvoBarChart
      showTooltip
      title={title}
      startingDataColor={startingDataColor}
      dataSource={ds}
      onPointClick={(e) => onItemClick?.(e.target.data as DashboardStatsPoint)}>
      <ValueAxis
        title={setup.valueAxis}
        type={shouldUseLogScale ? 'logarithmic' : undefined}
        logarithmBase={10}
        tickInterval={shouldUseLogScale ? undefined : yTickInterval}
        visualRange={
          shouldUseLogScale
            ? undefined
            : {
                endValue: (yTickInterval ?? 0) + (maxBy(ds, (item) => item.value)?.value || 0),
              }
        }
      />
      <ArgumentAxis
        title={setup.argumentAxis}
        label={{
          rotationAngle: 45,
          indentFromAxis: 0,
          overlappingBehavior: 'rotate',
        }}
      />
      <Series
        argumentField="label"
        valueField="value"
        name={setup.valueAxis}
        label={{
          visible: true,
        }}
      />
    </NuvoBarChart>
  );
};

export default React.memo(BarChart);
